import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { CasinoOpenBet, casinoAnalysisApi } from "src/api/casinoAnalysisApi";
import parseNumber from "src/utils/parse-number";
import { ErrorBlock, LoadingBlock } from "./ui-blocks";

type CasinoAnalysisLocationState = {
  providerName?: string;
  providerGameId?: string;
  eventName?: string;
  providerType?: string;
};

const DEFAULT_CLIENT_IP = "127.0.0.1";
const DEFAULT_IS_MOBILE = false;
const DEFAULT_POLL_MS = 5000;
const getCasinoProviderType = (providerType?: string) =>
  providerType?.trim().toLowerCase() === "aura" ? "Aura" : "Evolution";

const readRate = (bet: CasinoOpenBet) => bet.rate ?? bet.odds ?? "-";
const readAmount = (bet: CasinoOpenBet) => Number(parseNumber({ value: bet.amount ?? bet.bet_amount, type: "float" })).toFixed(2);
const readUserName = (bet: CasinoOpenBet) => bet.user_name || bet.userName || "-";
const readMarket = (bet: CasinoOpenBet) => bet.market || bet.market_name || "-";
const readRunner = (bet: CasinoOpenBet) => bet.runner || bet.runner_name || "-";

const CasinoAnalysisUserPage = () => {
  const { providerEventId = "" } = useParams<{ providerEventId: string }>();
  const location = useLocation();
  const state = (location.state as CasinoAnalysisLocationState | null) || null;
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [iframeUrl, setIframeUrl] = useState("");
  const [loadingIframe, setLoadingIframe] = useState(true);
  const [iframeError, setIframeError] = useState("");

  const [bets, setBets] = useState<CasinoOpenBet[]>([]);
  const [loadingBets, setLoadingBets] = useState(true);
  const [betsError, setBetsError] = useState("");

  const betsRequestRef = useRef(0);

  const providerName = state?.providerName || query.get("providerName") || "default provider";
  const providerType = getCasinoProviderType(state?.providerType || query.get("providerType") || "");
  const gameLabel = state?.eventName || query.get("eventName") || state?.providerGameId || query.get("providerGameId") || providerEventId;

  const casinoConfig = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return {
      redirectUrl: `${origin}/casinoanalysisuser/${providerEventId}`,
      clientIp: DEFAULT_CLIENT_IP,
      isMobile: DEFAULT_IS_MOBILE,
      pollMs: DEFAULT_POLL_MS,
    };
  }, [providerEventId]);

  const fetchIframeUrl = useCallback(() => {
    if (!providerEventId) {
      setIframeError("Provider event id is missing.");
      setLoadingIframe(false);
      return () => undefined;
    }
    const controller = new AbortController();
    setLoadingIframe(true);
    setIframeError("");

    casinoAnalysisApi
      .fetchCasinoUrl(
        {
          gameId: providerEventId,
          provider_type: providerType,
          providerName,
          is_mobile: casinoConfig.isMobile,
          clientIp: casinoConfig.clientIp,
          redirectUrl: casinoConfig.redirectUrl,
        },
        { signal: controller.signal }
      )
      .then((response) => {
        if (!controller.signal.aborted) {
          setIframeUrl(response.url || "");
          if (!response.url) {
            setIframeError("Casino URL was empty.");
          }
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : "Unable to load casino URL.";
        setIframeError(message);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingIframe(false);
        }
      });

    return () => controller.abort();
  }, [casinoConfig.clientIp, casinoConfig.isMobile, casinoConfig.redirectUrl, providerEventId, providerName, providerType]);

  const fetchOpenBets = useCallback(
    async (isInitialLoad = false) => {
      if (!providerEventId) {
        setLoadingBets(false);
        setBetsError("Provider event id is missing.");
        return;
      }

      const requestId = betsRequestRef.current + 1;
      betsRequestRef.current = requestId;
      const controller = new AbortController();

      if (isInitialLoad) {
        setLoadingBets(true);
      }

      try {
        const list = await casinoAnalysisApi.fetchCasinoOpenBets(providerEventId, { signal: controller.signal });
        if (requestId !== betsRequestRef.current) return;
        setBets(list);
        setBetsError("");
      } catch (err: unknown) {
        if (controller.signal.aborted || requestId !== betsRequestRef.current) return;
        const message = err instanceof Error ? err.message : "Unable to load open bets.";
        setBetsError(message);
      } finally {
        if (isInitialLoad && requestId === betsRequestRef.current) {
          setLoadingBets(false);
        }
      }
    },
    [providerEventId]
  );

  useEffect(() => {
    const cleanup = fetchIframeUrl();
    return cleanup;
  }, [fetchIframeUrl]);

  useEffect(() => {
    fetchOpenBets(true);
    const intervalId = window.setInterval(() => {
      fetchOpenBets(false);
    }, casinoConfig.pollMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [casinoConfig.pollMs, fetchOpenBets]);

  return (
    <div className="main-content">
      <div className="page-content">
        <div>
          <div className="detail-page-container">
            <div className="center-main-container">
              <div className="center-content">
                <div className="casino-container" style={{ width: "100%", height: "100%", overflow: "hidden", position: "relative" }}>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h4 className="mb-0 font-size-18">Casino Analysis - {gameLabel}</h4>
                    <small className="text-muted">Provider: {providerName}</small>
                  </div>

                  {loadingIframe ? <LoadingBlock message="Loading casino iframe..." /> : null}
                  {!loadingIframe && iframeError ? <ErrorBlock message={iframeError} onRetry={fetchIframeUrl} /> : null}
                  {!loadingIframe && !iframeError && iframeUrl ? (
                    <iframe
                      title={`casino-event-${providerEventId}`}
                      src={iframeUrl}
                      style={{
                        width: "100%",
                        height: "75vh",
                        border: 0,
                        borderRadius: 4,
                        background: "#fff",
                        transform: "scale(1.40)",
                        transformOrigin: "top",
                      }}
                      allow="fullscreen"
                    />
                  ) : null}
                </div>
              </div>

              <div className="right-sidebar">
                <div className="right-sidebar">
                  <div data-simplebar="init">
                    <div className="casino-right-sidebar">
                      <div className="card m-b-10 my-bet">
                        <div className="card-header">
                          <h6 className="card-title float-left">My Bets</h6>
                          <button type="button" className="btn btn-back float-right" onClick={() => fetchOpenBets(false)}>
                            View More
                          </button>
                        </div>

                        <div className="card-body1">
                          <div className="tab-content">
                            <div id="matched-bet" className="tab-pane active">
                              {loadingBets ? <LoadingBlock message="Loading open bets..." /> : null}
                              {!loadingBets && betsError ? <ErrorBlock message={betsError} /> : null}

                              <div className="table-responsive1">
                                <table className="table coupon-table table-striped mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ minWidth: 90 }}>UserName</th>
                                      <th style={{ minWidth: 90 }}>Nation</th>
                                      <th className="text-right" style={{ minWidth: 50 }}>
                                        Rate
                                      </th>
                                      <th className="text-right" style={{ minWidth: 70 }}>
                                        Amount
                                      </th>
                                      <th className="text-right" style={{ minWidth: 70 }}>
                                        PlaceDate
                                      </th>
                                      <th className="text-right" style={{ minWidth: 70 }}>
                                        Gametype
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!bets.length ? (
                                      <tr>
                                        <td colSpan={6} className="text-center">
                                          No records found
                                        </td>
                                      </tr>
                                    ) : (
                                      bets.map((bet, index) => (
                                        <tr key={`${readUserName(bet)}-${index}`}>
                                          <td>{readUserName(bet)}</td>
                                          <td>{readRunner(bet)}</td>
                                          <td className="text-right">{readRate(bet)}</td>
                                          <td className="text-right">{readAmount(bet)}</td>
                                          <td className="text-right">-</td>
                                          <td className="text-right">-</td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasinoAnalysisUserPage;
